import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { User as SupabaseUser, Session } from '@supabase/supabase-js'
import type { User, Organization } from '@/types'
import { CORNERSTONE_ORG_ID } from '@/lib/constants'

interface AuthState {
  session: Session | null
  supabaseUser: SupabaseUser | null
  user: User | null
  organization: Organization | null
  isLoading: boolean
  isInitialized: boolean

  // Actions
  setSession: (session: Session | null) => void
  setSupabaseUser: (user: SupabaseUser | null) => void
  setUser: (user: User | null) => void
  setOrganization: (organization: Organization | null) => void
  setIsLoading: (isLoading: boolean) => void
  setIsInitialized: (isInitialized: boolean) => void
  reset: () => void
}

const initialState = {
  session: null,
  supabaseUser: null,
  user: null,
  organization: null,
  isLoading: true,
  isInitialized: false,
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      ...initialState,

      setSession: (session) => set({ session }),
      setSupabaseUser: (supabaseUser) => set({ supabaseUser }),
      setUser: (user) => set({ user }),
      setOrganization: (organization) => set({ organization }),
      setIsLoading: (isLoading) => set({ isLoading }),
      setIsInitialized: (isInitialized) => set({ isInitialized }),
      reset: () => set(initialState),
    }),
    {
      name: 'cornerstone-auth',
      partialize: () => ({}),
    }
  )
)

// Selectors
export const selectIsAuthenticated = (state: AuthState) =>
  !!state.session && !!state.user

export const selectUserRole = (state: AuthState) => state.user?.role ?? null

export const selectIsAdmin = (state: AuthState) =>
  state.user?.role === 'admin' || state.user?.role === 'account_admin'

export const selectIsPlatformAdmin = (state: AuthState) =>
  !!(state.user as any)?.is_platform_admin

export const selectIsCornerstoneOrg = (state: AuthState) =>
  state.user?.organization_id === CORNERSTONE_ORG_ID

// Portal type selectors (replaces old role-based portal detection)
export const selectPortalType = (state: AuthState) =>
  state.user?.portal_type ?? 'internal'

export const selectIsVendorPortal = (state: AuthState) =>
  state.user?.portal_type === 'vendor'

export const selectIsClientPortal = (state: AuthState) =>
  state.user?.portal_type === 'client'

export const selectIsPortalUser = (state: AuthState) =>
  state.user?.portal_type === 'vendor' || state.user?.portal_type === 'client'

export const selectIsInternalUser = (state: AuthState) =>
  state.user?.portal_type === 'internal' || !state.user?.portal_type

// Page permission selectors
export const selectPagePermissions = (state: AuthState) =>
  state.user?.page_permissions ?? ['*']

export const selectCanAccessPage = (page: string) => (state: AuthState) => {
  if (state.user?.role === 'admin' || state.user?.role === 'account_admin') return true
  const perms = state.user?.page_permissions ?? ['*']
  return perms.includes('*') || perms.includes(page)
}

// Legacy compat — map to new model
export const selectCanManageClients = (state: AuthState) =>
  selectIsAdmin(state) || selectCanAccessPage('clients')(state)

export const selectCanManageEstimates = (state: AuthState) =>
  selectIsAdmin(state) || selectCanAccessPage('estimates')(state)

export const selectCanInspectEquipment = (state: AuthState) =>
  selectIsAdmin(state) || selectCanAccessPage('equipment')(state)
